import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../stores/cartStore';
import CartItem from './CartItem';
import CartSummary from './CartSummary';

const Cart = ({ isOpen, onClose }) => {
  const { items, totalItems, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose(); // Close the cart
    navigate('/checkout'); // Navigate to checkout page
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Cart Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Shopping Cart ({totalItems})
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Cart Content */}
        <div className="flex-1 flex flex-col min-h-0">
          {items.length === 0 ? (
            /* Empty Cart */
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.293 2.293A1 1 0 007 16h10a1 1 0 100 2H7a3 3 0 01-3-3V6a1 1 0 011-1z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-600 mb-2">Your cart is empty</h3>
              <p className="text-gray-500 mb-4">Add some products to get started!</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              {/* Cart Items - Scrollable Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                {items.map(item => (
                  <CartItem key={item.id} item={item} />
                ))}
              </div>

              {/* Cart Summary and Actions - Fixed at Bottom */}
              <div className="border-t border-gray-200 p-4 space-y-4 flex-shrink-0 bg-white">
                <CartSummary totalPrice={totalPrice} />
                
                {/* Action Buttons */}
                <div className="space-y-2">
                  <button
                    onClick={handleCheckout}
                    className="w-full py-3 px-4 rounded-md font-semibold transition-colors bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Proceed to Checkout
                  </button>
                  
                  <button
                    onClick={onClose}
                    className="w-full py-2 px-4 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Continue Shopping
                  </button>
                  
                  {items.length > 0 && (
                    <button
                      onClick={clearCart}
                      className="w-full py-2 px-4 text-red-600 hover:text-red-700 transition-colors text-sm"
                    >
                      Clear Cart
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;
